import { getApiHttpStatus, getSurfaceableApiDetail } from 'helpers/rtk-utils';
import {
  AdminFilter,
  AdminFilterCreatePayload,
  AdminFilterUpdatePayload,
  FilterType,
} from 'store/clientFilters/types';
import { FILTER_NAME_TAKEN, FILTER_SAVE_ERROR, REQUIRED_RECIPIENTS } from '../constants';
import { FilterFormValues, RecipientOption } from './types';

export const toInitialValues = (
  filter: AdminFilter | undefined,
  toRecipientOption: (recipientId: string) => RecipientOption
): FilterFormValues =>
  filter
    ? {
        filter_name: filter.filter_name,
        filter_type: filter.filter_type,
        recipients: filter.recipient_ids.map(toRecipientOption),
        is_active: filter.status === 'ACTIVE',
      }
    : { filter_name: '', filter_type: '', recipients: [], is_active: true };

const toRecipientIds = (recipients: RecipientOption[]) => recipients.map(({ value }) => value);

const hasSameIds = (left: string[], right: string[]) => {
  const rightIds = new Set(right);
  return left.length === right.length && left.every(id => rightIds.has(id));
};

export const buildCreatePayload = (values: FilterFormValues): AdminFilterCreatePayload => ({
  filter_name: values.filter_name.trim(),
  filter_type: values.filter_type as FilterType,
  recipient_ids: toRecipientIds(values.recipients),
});

/** Only the fields that differ from the saved filter, since the PATCH keeps whatever is omitted. */
export const buildUpdatePayload = (
  filter: AdminFilter,
  values: FilterFormValues
): AdminFilterUpdatePayload => {
  const payload: AdminFilterUpdatePayload = {};
  const filterName = values.filter_name.trim();
  const status = values.is_active ? 'ACTIVE' : 'PAUSED';
  const recipientIds = toRecipientIds(values.recipients);

  if (filterName !== filter.filter_name) payload.filter_name = filterName;
  if (values.filter_type !== filter.filter_type) {
    payload.filter_type = values.filter_type as FilterType;
  }
  if (status !== filter.status) payload.status = status;
  if (!hasSameIds(recipientIds, filter.recipient_ids)) payload.recipient_ids = recipientIds;

  return payload;
};

export const isRenamed = (originalName: string, filterName: string | undefined) => {
  const trimmedName = filterName?.trim() ?? '';
  return trimmedName !== '' && trimmedName !== originalName;
};

export const requireRecipients = (recipients: RecipientOption[] | undefined) =>
  recipients?.length ? undefined : REQUIRED_RECIPIENTS;

export const describeFilterSaveFailure = (error: unknown) => {
  if (getApiHttpStatus(error) === 409) {
    return { message: FILTER_NAME_TAKEN, isNameTaken: true, isExpected: true };
  }
  // 404: the client lost its cart, or the filter is gone, since the page loaded.
  const detail = getSurfaceableApiDetail(error, [400, 404]);
  if (detail) return { message: detail, isNameTaken: false, isExpected: true };
  return { message: FILTER_SAVE_ERROR, isNameTaken: false, isExpected: false };
};
